/**
 * CLI Auth Page — browser-side of the SDK browser login flow.
 *
 * Reached via /auth/cli?port={port}. After the user logs in (Supabase),
 * this page auto-generates an API key and POSTs it back to the SDK's
 * local callback server on localhost:{port}/callback.
 */

import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { createApiKey } from "../api/apiKeys";

type Status = "waiting" | "creating_key" | "sending" | "done" | "error";

export default function CLIAuthPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const port = searchParams.get("port");
  const [status, setStatus] = useState<Status>("waiting");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!port) {
      setError("Missing port parameter. Please retry from the SDK.");
      setStatus("error");
      return;
    }

    async function handleAuth() {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        // Save CLI return path so AuthCallback redirects back here after login.
        // Then send the user to the sign-in page where they can pick any method
        // (Google, GitHub, Flow360).
        const base = import.meta.env.BASE_URL.replace(/\/$/, "");
        const fullPath = `${window.location.pathname}${window.location.search}`;
        const routerPath = fullPath.startsWith(base) ? fullPath.slice(base.length) : fullPath;
        sessionStorage.setItem("cli_auth_return", routerPath);
        navigate("/", { replace: true });
        return;
      }

      try {
        setStatus("creating_key");
        const hostname = new URL(window.location.href).hostname;
        const keyName = `CLI login (${hostname}, ${new Date().toISOString().slice(0, 16)})`;
        const created = await createApiKey({ name: keyName });

        setStatus("sending");
        // Navigate instead of fetch — browsers block HTTPS→HTTP fetch (mixed content)
        // but allow normal navigations to localhost.
        window.location.href = `http://localhost:${port}/callback?api_key=${encodeURIComponent(created.raw_key)}`;
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to complete CLI login");
        setStatus("error");
      }
    }

    handleAuth();
  }, [port]);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        gap: 16,
        fontFamily: "system-ui, -apple-system, sans-serif",
      }}
    >
      {status === "waiting" && (
        <p style={{ color: "var(--gray-9, #6b7280)" }}>Checking authentication...</p>
      )}
      {status === "creating_key" && (
        <p style={{ color: "var(--gray-9, #6b7280)" }}>Creating API key...</p>
      )}
      {status === "sending" && (
        <p style={{ color: "var(--gray-9, #6b7280)" }}>Sending key to SDK...</p>
      )}
      {status === "done" && (
        <>
          <div style={{ fontSize: 48 }}>&#x2713;</div>
          <p style={{ fontSize: 18, fontWeight: 600, color: "var(--green-9, #16a34a)" }}>
            Authenticated! You can close this tab.
          </p>
          <p style={{ color: "var(--gray-9, #6b7280)", fontSize: 14 }}>
            Your API key has been saved to ~/.config/thread/credentials.toml
          </p>
        </>
      )}
      {status === "error" && (
        <>
          <p style={{ color: "var(--red-9, #dc2626)" }}>{error}</p>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: "8px 16px",
              borderRadius: 8,
              border: "1px solid var(--gray-7, #d1d5db)",
              background: "var(--gray-3, #f3f4f6)",
              color: "var(--gray-12, #111827)",
              cursor: "pointer",
            }}
          >
            Retry
          </button>
        </>
      )}
    </div>
  );
}
